/**
 * The tiles a machine reaches, as a little map in the same shape as on the planet: its own tile in
 * the middle (the machine drawn on it), and each tile in what it is to the machine: for an
 * extractor the ground it draws from glowing (shared ground striped), for a charge the rock it
 * breaks glowing; the rest in its own colour, dimmed.
 */
import type { JSX } from 'react';

import { hexString, TILE_TOP } from '@core/palette';
import type { ReachTile } from '@game/planet/reach';

/** Each tile drawn this much smaller round its middle: a gap between tiles, as on the planet. */
const INSET = 0.86;

const centroid = (points: ReachTile['points']): [number, number] => {
  let x = 0;
  let y = 0;
  for (const [px, py] of points) {
    x += px;
    y += py;
  }
  return [x / points.length, y / points.length];
};

/** Its outline, inset round its middle, as SVG points (y turned down). */
function outline(points: ReachTile['points']): string {
  const [cx, cy] = centroid(points);
  return points
    .map(([x, y]) => {
      const ix = cx + (x - cx) * INSET;
      const iy = cy + (y - cy) * INSET;
      return `${ix.toFixed(4)},${(-iy).toFixed(4)}`;
    })
    .join(' ');
}

export function ReachMap({
  tiles,
  self: own,
}: {
  tiles: readonly ReachTile[];
  /** The machine's own tile. */
  self: number;
}): JSX.Element | null {
  const self = tiles.find((t) => t.tile === own);
  if (!self) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of tiles)
    for (const [x, y] of t.points) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, -y);
      maxY = Math.max(maxY, -y);
    }
  const pad = (maxX - minX) * 0.04;
  const [sx, sy] = centroid(self.points);
  const r = Math.max(...self.points.map(([x, y]) => Math.hypot(x - sx, y - sy)));
  const stripe = r * 0.22;
  return (
    <svg
      className="reach-map"
      viewBox={`${(minX - pad).toFixed(4)} ${(minY - pad).toFixed(4)} ${(maxX - minX + pad * 2).toFixed(4)} ${(maxY - minY + pad * 2).toFixed(4)}`}
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="reach-shared"
          patternUnits="userSpaceOnUse"
          width={stripe}
          height={stripe}
          patternTransform="rotate(45)"
        >
          <rect width={stripe} height={stripe} className="shared-a" />
          <rect width={stripe / 2} height={stripe} className="shared-b" />
        </pattern>
      </defs>
      {tiles.map((t) => (
        <polygon
          key={t.tile}
          className={`tile ${t.role}`}
          points={outline(t.points)}
          style={t.role === 'other' ? { fill: hexString(TILE_TOP[t.kind]) } : undefined}
        />
      ))}
      <circle className="machine" cx={sx} cy={-sy} r={r * 0.34} strokeWidth={r * 0.1} />
      <circle className="machine-core" cx={sx} cy={-sy} r={r * 0.14} />
    </svg>
  );
}
